/**
 * Desktop-only: proactively hand the local backend a FRESH Clerk session token.
 *
 * The renderer holds the live Clerk session; the spawned local backend does not,
 * yet it must mint a per-user AI-relay bearer from a CURRENT Clerk token. The
 * backend also captures `x-clerk-token` off ordinary request traffic, but during a
 * long idle (or before the first request of a session) that store can go stale, so
 * this pushes a fresh token down through the desktop bridge
 * (`pushRelayClerkToken` → IPC → main → loopback POST) on a short interval and
 * once up front.
 *
 * No secret ever leaves the renderer — only the Clerk session token, which the
 * backend forwards to the hosted mint endpoint that verifies it. Best-effort and
 * fully guarded: a no-op on web, on older desktop builds lacking the bridge
 * method, and whenever there is no signed-in session.
 */
import { getClerkToken, whenClerkReady } from "./clerkTokenBridge";

// Clerk session tokens live ~60s; refresh comfortably inside that window.
const PUSH_INTERVAL_MS = 45_000;

let started = false;

type RelayTokenBridge = {
  readonly pushRelayClerkToken?: (token: string) => Promise<boolean>;
};

function relayTokenBridge(): RelayTokenBridge | null {
  if (typeof window === "undefined") return null;
  const bridge = (window as { desktopBridge?: RelayTokenBridge }).desktopBridge;
  return bridge && typeof bridge.pushRelayClerkToken === "function" ? bridge : null;
}

async function pushOnce(bridge: RelayTokenBridge): Promise<void> {
  try {
    const token = await getClerkToken();
    if (!token) return;
    await bridge.pushRelayClerkToken?.(token);
  } catch {
    // Best-effort: a failed push degrades to the backend's header-capture
    // backstop and, ultimately, a clear AI-call auth error — never a crash.
  }
}

/**
 * Start the desktop relay Clerk-token push loop. Idempotent; safe to call on web
 * (it simply does nothing when the desktop bridge method is absent).
 */
export function installRelayClerkTokenPush(): void {
  if (started) return;
  const bridge = relayTokenBridge();
  if (!bridge) return;
  started = true;

  void (async () => {
    await whenClerkReady();
    await pushOnce(bridge);
  })();

  setInterval(() => {
    void pushOnce(bridge);
  }, PUSH_INTERVAL_MS);
}
